import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Globe2, ArrowRight, CheckCircle2 } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { Button } from "@/components/ui/button";


// Country breakdown — the five flagship packs shipped so far. Update
// this table when a new flagship seed lands (add a row, bump total on
// the hero counter). Order runs west → east so the layout roughly
// mirrors the international-family search journey.
const FLAGSHIP_PACKS = [
  {
    slug: "us-ivyplus", country: "United States",
    count: 14, flag: "🇺🇸",
    tagline: "Ivy League + MIT / Stanford / Chicago / Caltech / Duke / Berkeley",
    unis: ["Harvard", "Yale", "Princeton", "Columbia", "Penn", "Cornell",
           "Dartmouth", "Brown", "MIT", "Stanford", "Chicago", "Caltech",
           "Duke", "Berkeley"],
  },
  {
    slug: "uk-russell-ten", country: "United Kingdom",
    count: 10, flag: "🇬🇧",
    tagline: "Oxbridge + Golden Triangle + Russell Group leaders",
    unis: ["Oxford", "Cambridge", "Imperial", "LSE", "UCL",
           "Edinburgh", "KCL", "Manchester", "Bristol", "Warwick"],
  },
  {
    slug: "fr-top12", country: "France",
    count: 12, flag: "🇫🇷",
    tagline: "PSL + Sciences Po + Grandes Écoles + Business Schools",
    unis: ["PSL", "Sciences Po", "Polytechnique", "Sorbonne",
           "Paris-Saclay", "CentraleSupélec", "Paris Cité", "HEC",
           "ESSEC", "ESCP", "EDHEC", "ENS Paris"],
  },
  {
    slug: "nl-wo14", country: "Netherlands",
    count: 26, flag: "🇳🇱",
    tagline: "The thirteen Dutch WO research universities, two flagships each",
    unis: ["TU Delft", "TU Eindhoven", "Groningen", "Utrecht", "UvA",
           "VU Amsterdam", "Wageningen", "Twente", "Tilburg", "Erasmus",
           "Leiden", "Maastricht", "Radboud"],
  },
  {
    slug: "de-top12", country: "Germany",
    count: 12, flag: "🇩🇪",
    tagline: "TU9 + LMU / Heidelberg / Freiburg + Mannheim / Frankfurt",
    unis: ["TUM", "RWTH Aachen", "LMU Munich", "Heidelberg", "KIT",
           "TU Berlin", "Karlsruhe UAS", "Freiburg", "Tübingen",
           "Mannheim", "Frankfurt", "Bonn"],
  },
  {
    slug: "au-g8", country: "Australia",
    count: 8, flag: "🇦🇺",
    tagline: "The Group of Eight research universities — Sandstone campuses",
    unis: ["Melbourne", "Sydney", "ANU", "UNSW", "Monash",
           "UQ", "Adelaide", "UWA"],
  },
  {
    slug: "ca-u15", country: "Canada",
    count: 8, flag: "🇨🇦",
    tagline: "U15 research anchors — Waterloo Co-op, McMaster BHSc, Smith Commerce",
    unis: ["Toronto", "McGill", "UBC", "Waterloo",
           "Alberta", "Montréal", "Queen's", "McMaster"],
  },
  {
    slug: "sg-apac4", country: "Singapore",
    count: 4, flag: "🇸🇬",
    tagline: "Autonomous research + specialist unis — NUS / NTU / SMU / SUTD-MIT",
    unis: ["NUS", "NTU", "SMU", "SUTD"],
  },
  {
    slug: "hk-cluster", country: "Hong Kong",
    count: 4, flag: "🇭🇰",
    tagline: "Global-rank research + applied unis — HKU, CUHK, HKUST, PolyU",
    unis: ["HKU", "CUHK", "HKUST", "PolyU"],
  },
  {
    slug: "ie-iua5", country: "Ireland",
    count: 5, flag: "🇮🇪",
    tagline: "IUA research anchors — Trinity Mods, UCD Actuarial, Galway Medtech",
    unis: ["Trinity", "UCD", "UCC", "Galway", "DCU"],
  },
  {
    slug: "ch-eth-cluster", country: "Switzerland",
    count: 5, flag: "🇨🇭",
    tagline: "ETH-cluster + specialist unis — ETH Zürich, EPFL, UZH, UNIGE, HSG",
    unis: ["ETH Zürich", "EPFL", "UZH", "UNIGE", "HSG"],
  },
  {
    slug: "nordic-trio", country: "Nordics",
    count: 5, flag: "🇸🇪🇩🇰🇳🇴",
    tagline: "SE / DK / NO anchors — KTH, Lund, KU, DTU, NTNU",
    unis: ["KTH", "Lund", "KU Copenhagen", "DTU", "NTNU"],
  },
  {
    slug: "is-reykjavik", country: "Iceland",
    count: 2, flag: "🇮🇸",
    tagline: "HÍ (last free-tuition Nordic) + RU — Reykjavík's two anchor CS BScs",
    unis: ["Háskóli Íslands (HÍ)", "Reykjavík University (RU)"],
  },
];

const TOTAL_FLAGSHIPS = FLAGSHIP_PACKS.reduce((s, p) => s + p.count, 0);
const TOTAL_COUNTRIES = FLAGSHIP_PACKS.length;


export default function FlagshipsLanding() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title={`${TOTAL_FLAGSHIPS} Hand-Checked Flagship Degrees · WhichUniversityAdvisor`}
        description={`Editorially verified flagship undergraduate programmes across ${TOTAL_COUNTRIES} countries — Oxbridge, Ivy League, TU9, Grande École, Dutch WO-14. Every tuition, entry requirement and offer rate cross-checked against the university's own page.`}
        path="/flagships"
      />

      {/* HERO */}
      <section
        data-testid="flagships-hero"
        className="grid md:grid-cols-3 gap-10 items-start"
      >
        <div className="md:col-span-2">
          <div className="label-uppercase text-emerald-700 inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Trust-tier catalogue
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight tracking-tight">
            The <em>hand-checked</em> flagship 50<span className="text-emerald-700">+</span>.
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            Every year, WhichUniversityAdvisor's editors sit down with the university's
            own study-programme pages and hand-check the {TOTAL_FLAGSHIPS} flagship
            bachelors that anchor an international family's global shortlist.
            Tuition, entry requirements, offer rate, IELTS band, application
            deadlines and post-study work rules — all cross-referenced against
            the source, not scraped from an aggregator.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-full px-3 py-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="font-underground-sc tracking-wider">Verified against uni's own page</span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Globe2 className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">{TOTAL_COUNTRIES} countries · {TOTAL_FLAGSHIPS} flagships</span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Sparkles className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">Editorially curated · no aggregator listings</span>
            </span>
          </div>
          <div className="mt-8">
            <Link to="/advanced?preset=flagships">
              <Button
                data-testid="flagships-hero-cta"
                size="lg"
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
              >
                Browse the Flagship Catalogue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        <aside
          data-testid="flagships-hero-counter"
          className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6"
        >
          <div className="label-uppercase !text-[10px] text-stone-500">
            Currently in the flagship catalogue
          </div>
          <div className="font-editorial text-6xl mt-2 text-emerald-800">
            {TOTAL_FLAGSHIPS}
          </div>
          <div className="text-sm text-stone-600 mt-2">
            hand-checked bachelors across{" "}
            <span className="font-semibold">{TOTAL_COUNTRIES} countries</span>
          </div>
          <ul className="mt-5 space-y-1 text-sm text-stone-700">
            {FLAGSHIP_PACKS.map((p) => (
              <li
                key={p.slug}
                className="flex items-center justify-between border-b border-stone-200 last:border-0 pb-1"
              >
                <span>
                  <span className="mr-2">{p.flag}</span>
                  {p.country}
                </span>
                <span className="font-mono text-emerald-800">{p.count}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-stone-500 leading-relaxed">
            One click below to load the entire catalogue with all filters
            pre-applied — no wizard, no sign-up.
          </p>
        </aside>
      </section>

      {/* WHY THIS EXISTS */}
      <section className="mt-16 grid md:grid-cols-3 gap-8">
        <div>
          <div className="label-uppercase text-stone-500">Why we built this</div>
          <h2 className="font-editorial text-2xl mt-2 leading-snug">
            Aggregators lie by omission.
          </h2>
          <p className="mt-3 text-stone-700 text-sm leading-relaxed">
            BachelorsPortal, StudyQA, TopUniversities, USNews — each one
            re-scrapes the same directory shells and quotes tuition that's
            three cycles old. When a family shortlists a flagship, they need
            2027-intake numbers, not 2021.
          </p>
        </div>
        <div>
          <div className="label-uppercase text-stone-500">How we verify</div>
          <h2 className="font-editorial text-2xl mt-2 leading-snug">
            Every field, every source URL.
          </h2>
          <p className="mt-3 text-stone-700 text-sm leading-relaxed">
            Each flagship carries a <code>source_url</code> pointing at the
            university's own programme page. Editors hand-check tuition, entry
            requirements, IELTS band, deadline and post-study visa rules and
            date-stamp the check. Whenever the source page changes, the
            record is flagged for re-verification.
          </p>
        </div>
        <div>
          <div className="label-uppercase text-stone-500">What's honest</div>
          <h2 className="font-editorial text-2xl mt-2 leading-snug">
            Every warning, upfront.
          </h2>
          <p className="mt-3 text-stone-700 text-sm leading-relaxed">
            Numerus fixus. Sperrkonto. F-1 OPT + STEM extension. Parcoursup
            deadlines. Concours interviews. Oxbridge test dates. Every
            flagship ships with the exact process warnings families forget
            about until it's too late.
          </p>
        </div>
      </section>

      {/* COUNTRY PACKS */}
      <section className="mt-16">
        <h2 className="font-editorial text-3xl leading-snug">
          The {TOTAL_COUNTRIES} packs, at a glance.
        </h2>
        <p className="mt-2 text-stone-600 max-w-3xl">
          Each pack picks the anchor undergraduate programme at each
          institution — the one international families search for by name.
        </p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FLAGSHIP_PACKS.map((p) => (
            <div
              key={p.slug}
              data-testid={`flagship-pack-${p.slug}`}
              className="rounded-lg border border-stone-300 bg-white p-5 hover:border-emerald-500 hover:shadow-sm transition"
            >
              <div className="flex items-baseline justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{p.flag}</span>
                  <h3 className="font-editorial text-xl">{p.country}</h3>
                </div>
                <span className="font-mono text-sm text-emerald-800">
                  {p.count} flagships
                </span>
              </div>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                {p.tagline}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5 text-[10px] font-underground-sc tracking-wider">
                {p.unis.map((u) => (
                  <Link
                    key={u}
                    to={`/explore?q=${encodeURIComponent(u)}`}
                    data-testid={`flagship-uni-chip-${p.slug}-${u.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-block bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5 text-stone-700 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-900 transition-colors"
                  >
                    {u}
                  </Link>
                ))}
              </div>
              {p.slug === "nordic-trio" && (
                <Link
                  to="/nordic-tuition-reality"
                  data-testid="nordic-trio-tuition-link"
                  className="mt-4 inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-amber-800 hover:text-amber-900 underline decoration-dotted"
                >
                  <span>⚠</span>
                  Read: Norway is no longer free (Aug 2023)
                </Link>
              )}
              {p.slug === "is-reykjavik" && (
                <Link
                  to="/nordic-flagship-corridor"
                  data-testid="is-reykjavik-corridor-link"
                  className="mt-4 inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-emerald-800 hover:text-emerald-900 underline decoration-dotted"
                >
                  <span>↝</span>
                  Read: the honest $550-to-$56k Nordic Corridor
                </Link>
              )}
              <div className="mt-4 pt-3 border-t border-stone-200">
                <Link
                  to={`/flagships/${p.slug}`}
                  data-testid={`flagship-pack-cta-${p.slug}`}
                  className="inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-emerald-700 hover:text-emerald-800"
                >
                  Browse the {p.count} {p.country} flagships
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mt-16 rounded-lg border border-emerald-300 bg-emerald-50 px-8 py-10 text-center">
        <ShieldCheck className="w-8 h-8 mx-auto text-emerald-700" />
        <h2 className="font-editorial text-3xl mt-3 leading-snug">
          Ready to browse the full catalogue?
        </h2>
        <p className="mt-2 text-stone-700 max-w-2xl mx-auto">
          One click — no wizard, no sign-up. The flagship filter is
          pre-applied so you land straight on the hand-checked shortlist.
        </p>
        <div className="mt-6">
          <Link to="/advanced?preset=flagships">
            <Button
              data-testid="flagships-cta-bottom"
              size="lg"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
            >
              Open the Flagship 50<span className="ml-1">+</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
